import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, SparklesIcon, WalletIcon, UserIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Lightweight Solo onboarding — 2 steps only.
// Skips company/address/team/permission setup since Solo plan is single-user.
// On completion, redirects straight to /dashboard.
const SoloOnboardingPage = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = welcome, 1 = profile/currency
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [baseCurrency, setBaseCurrency] = useState(user?.company?.base_currency || 'TRY');
  // Solo demo veri opt-in — default OFF (kullanıcı isterse örnek verilerle başlar)
  const [loadDemoData, setLoadDemoData] = useState(false);

  // Draft persistence key — kullanıcının email adresine göre izole edilir.
  // Böylece aynı tarayıcıda farklı hesaplar birbirinin draft'ını görmez.
  const DRAFT_KEY = `solo-onboarding-draft:${user?.id || user?.email || 'anon'}`;

  // Restore draft on mount (herhangi bir kayıtlı ilerleme varsa yükle).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.fullName) setFullName(draft.fullName);
      if (draft.phoneCountryCode) setPhoneCountryCode(draft.phoneCountryCode);
      if (draft.phoneNumber) setPhoneNumber(draft.phoneNumber);
      if (draft.baseCurrency) setBaseCurrency(draft.baseCurrency);
      if (typeof draft.loadDemoData === 'boolean') setLoadDemoData(draft.loadDemoData);
      if (typeof draft.step === 'number' && draft.step > 0) setStep(draft.step);
      toast.info('Kaldığınız yerden devam ediyorsunuz.', { autoClose: 2500 });
    } catch (e) {
      // Bozuk JSON — sessizce yoksay
    }
  }, []);

  // Kaydet ve Çık — mevcut form state'ini localStorage'a yaz, sonra çıkış.
  const saveDraftAndLeave = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        step, fullName, phoneCountryCode, phoneNumber, baseCurrency, loadDemoData,
        savedAt: new Date().toISOString(),
      }));
      toast.success('İlerlemeniz kaydedildi. Tekrar giriş yaptığınızda kaldığınız yerden devam edeceksiniz.');
    } catch (e) {
      // localStorage doluysa toast göster ama yine de çık
      toast.warn('Kayıt sırasında bir sorun oluştu, yine de çıkılıyor.');
    }
    setLeaveModal({ open: false, pendingAction: null });
    bypassGuardRef.current = true;
    setTimeout(() => {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }, 300);
  };

  // Reference data
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Sayfadan Ayrılma Koruması — kullanıcı Solo onboarding'i tamamlamadan
  // browser'ı kapatır, geri butonuna basar veya swipe yaparsa girdiği
  // bilgiler kaybolur. beforeunload (F5/close) native prompt tetikler,
  // popstate (back button/gesture) kendi TR modal'ımızı gösterir.
  const [leaveModal, setLeaveModal] = useState({ open: false, pendingAction: null });
  // Kullanıcı EVET'e bastığında popstate handler'ının modal'ı yeniden
  // açmasını engellemek için kullanılan bypass bayrağı.
  const bypassGuardRef = useRef(false);

  useEffect(() => {
    const beforeUnloadHandler = (e) => {
      e.preventDefault();
      e.returnValue = 'Onboarding turu tamamen bitmedi ve sayfadan ayrılmak istiyorsunuz. Emin misiniz?';
      return e.returnValue;
    };
    const pushGuard = () => {
      window.history.pushState({ onboardingGuard: true }, '', window.location.href);
    };
    const popStateHandler = () => {
      if (bypassGuardRef.current) {
        // Kullanıcı EVET dedi → bir sonraki popstate'te bypass et, sonra
        // bayrağı sıfırla ki başka bir back gesture yeniden modal açsın.
        bypassGuardRef.current = false;
        return;
      }
      pushGuard();
      setLeaveModal({ open: true, pendingAction: 'browser-back' });
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    window.addEventListener('popstate', popStateHandler);
    pushGuard();
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', popStateHandler);
    };
  }, []);

  const confirmLeave = () => {
    const { pendingAction } = leaveModal;
    setLeaveModal({ open: false, pendingAction: null });
    // Onboarding guard'ı geçici olarak bypass et, gerçek yönlendirmeyi yap.
    bypassGuardRef.current = true;
    if (pendingAction === 'browser-back') {
      // Guard state'i pop et — bu popstate bypass edilecek, sonrasında
      // gerçek önceki sayfa için tekrar back tetiklenir.
      window.history.back();
      // Ek olarak login'e yönlendir (kullanıcı onboarding'den çıkmak istedi):
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 50);
    } else if (pendingAction === 'logout') {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  };
  const cancelLeave = () => setLeaveModal({ open: false, pendingAction: null });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${BACKEND_URL}/api/country-phone-codes`, { headers })
      .then((r) => setPhoneCodes(r.data || []))
      .catch(() => setPhoneCodes([]));

    axios.get(`${BACKEND_URL}/api/currencies`, { headers })
      .then((r) => setCurrencies(r.data || []))
      .catch(() => setCurrencies([]));
  }, []);

  const popularCurrencies = ['TRY', 'USD', 'AED', 'EUR', 'GBP'];

  const handleFinish = async () => {
    if (!fullName.trim()) {
      toast.error('Lütfen ad-soyad bilgisini giriniz');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fullPhone = phoneNumber.trim()
        ? `${phoneCountryCode} ${phoneNumber.trim()}`
        : '';

      await axios.post(
        `${BACKEND_URL}/api/onboarding/complete`,
        {
          base_currency: baseCurrency,
          extra_currencies: [],
          // Solo: no company info — derive a sane default from user name
          company_name: fullName.trim(),
          address: '',
          tax_number: '',
          full_name: fullName.trim(),
          phone_number: fullPhone,
          load_demo_data: loadDemoData, // Kullanıcı opt-in ile demo veri
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (loadDemoData) {
        toast.success('Hoş geldin! Örnek verilerle başladık 🎉');
      } else {
        toast.success('Hoş geldin! 🚀');
      }
      // Draft'ı temizle — onboarding başarıyla tamamlandı.
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
      // Redirect to Dashboard directly (Solo users start on the dashboard,
      // not the expense entry — they may want to explore the empty state
      // or the demo data they just seeded before jumping into data entry).
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Solo onboarding error:', err);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 shadow-xl border-emerald-100" data-testid="solo-onboarding-card">
        {/* Progress */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="bg-emerald-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
              Solo Plan
            </span>
            <span className="text-xs text-gray-500">Hızlı kurulum</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${step >= 0 ? 'bg-emerald-500' : 'bg-gray-300'}`} />
            <span className={`w-2 h-2 rounded-full ${step >= 1 ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          </div>
        </div>

        {/* Step 0 — Welcome */}
        {step === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center py-6"
            data-testid="solo-onboarding-welcome"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <SparklesIcon className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Solo&apos;ya hoş geldin, {user?.full_name?.split(' ')[0] || ''} 👋
            </h1>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Tek kullanıcılık planın için gereken her şey hazır. Onay akışları, kullanıcı
              davetleri ve karmaşık ayarlar yok — sadece sen ve harcamaların.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-left">
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 mb-1" />
                <p className="text-sm font-medium text-gray-900">Onay akışı yok</p>
                <p className="text-xs text-gray-600">Anında kaydet, anında raporla</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 mb-1" />
                <p className="text-sm font-medium text-gray-900">Otomatik cari</p>
                <p className="text-xs text-gray-600">Kişisel & şirket ayrı</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 mb-1" />
                <p className="text-sm font-medium text-gray-900">Belge ekle</p>
                <p className="text-xs text-gray-600">Fiş & faturalarını topla</p>
              </div>
            </div>

            <Button
              onClick={() => setStep(1)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
              data-testid="solo-onboarding-start"
            >
              Hadi başlayalım
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* Step 1 — Profile + Currency */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="solo-onboarding-profile"
          >
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Hesabını tamamla</h2>
              <p className="text-sm text-gray-600">Sadece birkaç bilgi — sonra direkt ilk harcamana geçeceğiz.</p>
            </div>

            <div className="space-y-5">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <UserIcon className="w-4 h-4 inline mr-1 text-gray-500" />
                  Ad Soyad <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Ahmet Yılmaz"
                  data-testid="solo-onboarding-name"
                />
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon (Opsiyonel)
                </label>
                <div className="flex gap-2">
                  <select
                    value={phoneCountryCode}
                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                    className="w-28 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="solo-onboarding-phone-code"
                  >
                    <option value="+90">+90 🇹🇷</option>
                    {phoneCodes
                      .filter((p) => p.code !== '+90')
                      .slice(0, 50)
                      .map((p) => (
                        <option key={`${p.code}-${p.country_code}`} value={p.code}>
                          {p.code} {p.country_code}
                        </option>
                      ))}
                  </select>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="555 123 45 67"
                    className="flex-1"
                    data-testid="solo-onboarding-phone"
                  />
                </div>
              </div>

              {/* Base Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <WalletIcon className="w-4 h-4 inline mr-1 text-gray-500" />
                  Temel Para Birimi
                </label>
                <div className="flex gap-2 flex-wrap" data-testid="solo-onboarding-currency-group">
                  {popularCurrencies.map((code) => {
                    const cur = currencies.find((c) => c.code === code);
                    const symbol = cur?.symbol || code;
                    const selected = baseCurrency === code;
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setBaseCurrency(code)}
                        className={`px-4 py-2 rounded-lg border-2 transition ${
                          selected
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                        data-testid={`solo-onboarding-currency-${code.toLowerCase()}`}
                      >
                        <span className="font-medium">{code}</span>
                        <span className="text-gray-400 ml-1">{symbol}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Tüm harcamaların varsayılan para birimi olacak. Temel para birimi daha sonra değiştirilemez!
                </p>
              </div>

              {/* Demo veri opt-in — default OFF */}
              <div className="pt-2">
                <label className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={loadDemoData}
                    onChange={(e) => setLoadDemoData(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    data-testid="solo-onboarding-demo-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Örnek verilerle başla <span className="text-xs text-gray-500 font-normal">(opsiyonel)</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Uygulamanın nasıl çalıştığını detaylı şekilde görebilmek için ~150 harcama, 18 işletme ve 3 proje ile başla.
                      Bu özelliği istediğin zaman kapatabilirsin.
                    </p>
                    {!loadDemoData && (
                      <p className="text-xs text-emerald-700 mt-1 font-medium">
                        ✓ Varsayılan: Boş dashboard ile başlarsın
                      </p>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setStep(0)}
                className="text-sm text-gray-500 hover:text-gray-700"
                data-testid="solo-onboarding-back"
              >
                ← Geri
              </button>
              <Button
                onClick={handleFinish}
                disabled={saving || !fullName.trim()}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                data-testid="solo-onboarding-finish"
              >
                {saving ? 'Kaydediliyor...' : 'Bitir ve Yeni Harcama'}
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </Card>

      {/* Sayfadan Ayrılma Onay Modal'ı — minimal & merkezli tasarım. Üç
          seçenek: (1) Kaydet ve Çık — ilerleme localStorage'a yazılır ve
          kullanıcı geri döndüğünde kaldığı yerden devam eder; (2) İPTAL
          — modal kapanır, onboarding'de kalınır; (3) EVET, çık — ilerleme
          kaydedilmez ve /login'e yönlendirilir. */}
      {leaveModal.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="onboarding-leave-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Sayfadan ayrılmak üzeresiniz
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Onboarding tamamlanmadı. İlerlemenizi kaydedip sonra devam
              edebilir, yoksa tümüyle çıkabilirsiniz.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={saveDraftAndLeave}
                className="w-full px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition"
                data-testid="onboarding-leave-save"
              >
                Kaydet ve Çık
              </button>
              <button
                type="button"
                onClick={cancelLeave}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium transition"
                data-testid="onboarding-leave-cancel"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={confirmLeave}
                className="w-full px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 text-xs font-medium transition"
                data-testid="onboarding-leave-confirm"
              >
                Kaydetmeden çık
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoloOnboardingPage;
